import { ChevronDown, ChevronUp, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { useGuardarRellenoDocumentos, useRellenoDocumentos } from "@/hooks/useRellenoDocumentos";
import { getApiErrorMessage } from "@/lib/api";
import {
  entradasDe,
  mismasEntradas,
  moverEntrada,
  type DocumentoDelCatalogo,
  type EntradaDeLaLista,
  type PestanaDelRelleno,
} from "@/lib/rellenoDocumentos";

/* ---------------------------------------------------------------------------
   De qué documentos lee "Rellenar con IA", pestaña por pestaña (`/admin/ia`).

   Lo que se edita es UNA LISTA ORDENADA por pestaña: todos los documentos
   legibles de la lista se leen, y el número decide quién gana cuando dos se
   contradicen (y qué se recorta primero si el contexto no alcanza). Por eso el
   orden se ve como número, y mover es subir/bajar y no arrastrar: con el
   teclado y un lector de pantalla un botón con nombre es la única forma
   inequívoca de reordenar.

   Cada documento lleva una NOTA opcional: le dice a la IA qué buscar en ese
   documento. Viaja con su documento al moverlo.

   LAS EDICIONES SON LOCALES HASTA "Guardar", POR PESTAÑA. Un borrador por
   pestaña, en el padre, sobrevive al cambio de pestaña: si viviera en la
   tarjeta, al moverse entre pestañas se perdería lo editado sin aviso.

   Quitar se apaga con un solo documento: el servidor exige al menos uno, y un
   botón que siempre termina en 422 es peor que uno apagado.
--------------------------------------------------------------------------- */

const MAXIMO_DE_LA_NOTA = 500;

export function DocumentosDelRelleno() {
  const consulta = useRellenoDocumentos();
  const [pestanaId, setPestanaId] = useState<string | null>(null);
  const [borradores, setBorradores] = useState<Record<string, EntradaDeLaLista[]>>({});

  if (consulta.isPending) {
    return <Skeleton className="h-96 w-full rounded-xl" />;
  }

  if (consulta.isError) {
    return (
      <p className="text-destructive text-sm">
        No pudimos cargar los documentos de Rellenar con IA. Recarga la página para volver a
        intentarlo.
      </p>
    );
  }

  const pestanas = consulta.data?.pestanas ?? [];
  const catalogo = consulta.data?.catalogo ?? [];
  const pestana = pestanas.find((p) => p.id === pestanaId) ?? pestanas[0];

  if (!pestana) return null;

  return (
    <div className="grid items-start gap-5 lg:grid-cols-[17.5rem_1fr]">
      <nav className="space-y-1.5" aria-label="Pestañas de Rellenar con IA">
        {pestanas.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => setPestanaId(p.id)}
            aria-current={p.id === pestana.id ? "true" : undefined}
            className={`w-full rounded-lg border px-3.5 py-2.5 text-left text-sm font-bold transition-colors ${
              p.id === pestana.id ? "bg-card border-primary shadow-sm" : "border-transparent hover:bg-card"
            }`}
          >
            {p.etiqueta}
          </button>
        ))}
      </nav>

      <ListaDeLaPestana
        key={pestana.id}
        pestana={pestana}
        catalogo={catalogo}
        borrador={borradores[pestana.id]}
        onCambiar={(lista) => setBorradores((actuales) => ({ ...actuales, [pestana.id]: lista }))}
        onLimpiar={() =>
          setBorradores((actuales) => {
            const { [pestana.id]: _descartado, ...resto } = actuales;
            return resto;
          })
        }
      />
    </div>
  );
}

function ListaDeLaPestana({
  pestana,
  catalogo,
  borrador,
  onCambiar,
  onLimpiar,
}: {
  pestana: PestanaDelRelleno;
  catalogo: DocumentoDelCatalogo[];
  borrador: EntradaDeLaLista[] | undefined;
  onCambiar: (lista: EntradaDeLaLista[]) => void;
  onLimpiar: () => void;
}) {
  const guardar = useGuardarRellenoDocumentos();
  const [error, setError] = useState<string | null>(null);

  const guardadas = entradasDe(pestana.documentos);
  const lista = borrador ?? guardadas;
  const modificado = !mismasEntradas(lista, guardadas);

  /* Nombre de cada código: primero el catálogo vivo, y si el código ya no está
     (se dio de baja) el que el servidor mandó con el código por nombre. */
  const porCodigo = new Map<string, DocumentoDelCatalogo>();
  for (const d of catalogo) porCodigo.set(d.codigo, d);
  for (const d of pestana.documentos) if (!porCodigo.has(d.codigo)) porCodigo.set(d.codigo, d);

  const agregables = catalogo.filter((d) => !lista.some((e) => e.codigo === d.codigo));

  function cambiar(nueva: EntradaDeLaLista[]) {
    setError(null);
    onCambiar(nueva);
  }

  function guardarCambios() {
    setError(null);
    guardar.mutate(
      { pestana: pestana.id, lista },
      {
        onSuccess: () => {
          onLimpiar();
          toast.success("Documentos guardados.");
        },
        onError: (e) => setError(getApiErrorMessage(e, "No pudimos guardar los documentos.")),
      },
    );
  }

  return (
    <section className="bg-card space-y-4 rounded-xl border p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-bold">{pestana.etiqueta}</p>
          <p className="text-ink-muted mt-1 max-w-[70ch] text-sm text-pretty">
            Se leen todos los que se puedan leer y, si dos documentos se contradicen, manda el de
            número más bajo. La nota le dice a la IA qué buscar en ese documento.
          </p>
        </div>
        {pestana.personalizado ? <Badge variant="info">Personalizado</Badge> : null}
      </div>

      <ol aria-label="Documentos de la pestaña" className="space-y-2">
        {lista.map((entrada, indice) => {
          const documento = porCodigo.get(entrada.codigo);
          const nombre = documento?.nombre ?? entrada.codigo;
          const retirado = documento === undefined || documento.categoria === null;
          const idNota = `nota-${pestana.id}-${entrada.codigo}`;

          return (
            <li key={entrada.codigo} className="space-y-2 rounded-xl border px-3.5 py-2">
              <div className="flex items-center gap-3">
                <span className="text-ink-hint w-5 shrink-0 text-center text-sm font-bold tabular-nums">
                  {indice + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <p data-testid="documento-nombre" className="truncate text-sm font-bold">
                    {nombre}
                  </p>
                  {retirado ? (
                    <p className="text-warning-soft-foreground text-xs">
                      Ya no está en el catálogo: quítalo de la lista.
                    </p>
                  ) : (
                    <p className="text-ink-hint text-xs">
                      {documento.categoria === "legal" ? "Legal" : "Histórico"}
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Subir ${nombre}`}
                    disabled={indice === 0}
                    onClick={() => cambiar(moverEntrada(lista, indice, -1))}
                  >
                    <ChevronUp />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Bajar ${nombre}`}
                    disabled={indice === lista.length - 1}
                    onClick={() => cambiar(moverEntrada(lista, indice, 1))}
                  >
                    <ChevronDown />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Quitar ${nombre}`}
                    disabled={lista.length === 1}
                    onClick={() => cambiar(lista.filter((e) => e.codigo !== entrada.codigo))}
                  >
                    <X />
                  </Button>
                </div>
              </div>
              <div className="space-y-1.5 pl-8">
                <Label htmlFor={idNota}>Qué se busca en este documento (opcional)</Label>
                <Textarea
                  id={idNota}
                  value={entrada.nota}
                  maxLength={MAXIMO_DE_LA_NOTA}
                  rows={2}
                  onChange={(evento) =>
                    cambiar(
                      lista.map((e) =>
                        e.codigo === entrada.codigo ? { ...e, nota: evento.target.value } : e,
                      ),
                    )
                  }
                />
              </div>
            </li>
          );
        })}
      </ol>

      <div className="max-w-sm">
        <label
          htmlFor={`agregar-${pestana.id}`}
          className="text-ink-hint mb-1.5 block text-xs font-bold tracking-wide uppercase"
        >
          Agregar documento
        </label>
        <NativeSelect
          id={`agregar-${pestana.id}`}
          value=""
          disabled={agregables.length === 0}
          onChange={(evento) => {
            if (evento.target.value) cambiar([...lista, { codigo: evento.target.value, nota: "" }]);
          }}
        >
          <option value="">Elige un documento…</option>
          {agregables.map((d) => (
            <option key={d.codigo} value={d.codigo}>
              {d.nombre}
            </option>
          ))}
        </NativeSelect>
      </div>

      {error ? (
        <p role="alert" className="text-destructive text-sm">
          {error}
        </p>
      ) : null}

      {modificado ? (
        <div className="flex flex-wrap gap-2">
          <Button type="button" size="sm" disabled={guardar.isPending} onClick={guardarCambios}>
            {guardar.isPending ? <Spinner /> : null}
            Guardar
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setError(null);
              onLimpiar();
            }}
          >
            Descartar cambios
          </Button>
        </div>
      ) : null}
    </section>
  );
}
